'use client'

import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'

import { cn } from '../../utils/cn'
import { CheckIcon } from '../icons'

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  invalid?: boolean
  errorMessage?: string
  color?: string
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, color, children, invalid, errorMessage, ...props }, ref) => {
  const id = React.useId()

  return (
    <div className={className}>
      <div className="flex items-center">
        <CheckboxPrimitive.Root
          className={cn(
            invalid ? 'border-error-500' : 'border-neutral-600',
            props.checked
              ? color
                ? color
                : 'bg-periwinkle data-[disabled]:bg-neutral-600'
              : 'border-[1.5px] bg-white ',
            'flex shrink-0 h-6 w-6 appearance-none items-center justify-center rounded-[0.13rem] outline-none hover:ring-4 hover:ring-neutral-200 focus:ring-2 focus:ring-offset-2 focus:ring-[#3b82f680] disabled:border-neutral-600'
          )}
          id={`checkbox-${id}`}
          aria-describedby={`message-${id}`}
          {...props}
          ref={ref}
          aria-invalid={invalid}
        >
          <CheckboxPrimitive.Indicator className="flex h-full w-full items-center justify-center">
            <CheckIcon
              className="h-4 w-4 stroke-white stroke-[4px]"
              aria-label="check"
            />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>

        <label
          className={cn(
            'pl-3 cursor-pointer text-sm font-semibold',
            props.disabled && 'text-neutral-500',
            invalid && 'text-error-700'
          )}
          htmlFor={`checkbox-${id}`}
        >
          {children}
        </label>
      </div>

      {errorMessage && (
        <div
          className="text-error-700 text-xs"
          role="alert"
          id={`message-${id}`}
        >
          {errorMessage}
        </div>
      )}
    </div>
  )
})
Checkbox.displayName = 'Checkbox'

export { Checkbox }
